<template>
    <StepShell :embedded="embedded" tag="form" width="narrow" gap="none" @submit.prevent="handleSubmit">
        <StepHeader
            v-if="embedded"
            embedded
            :title="returning ? t('onboarding.calendarFirst.identityStep.titleReturning') : t('onboarding.calendarFirst.identityStep.title')"
            :lede="returning ? undefined : t('onboarding.calendarFirst.identityStep.lede')" />
        <div v-else class="heading">
            <BasePageHeader v-if="returning" :title="t('onboarding.calendarFirst.identityStep.titleReturning')" no-border />
            <template v-else>
                <BasePageHeader :title="t('onboarding.calendarFirst.identityStep.title')" :subtitle="t('onboarding.calendarFirst.identityStep.lede')" no-border subtitle-below />
                <p class="intro">{{ introCopy }}</p>
            </template>
        </div>
        <p v-if="embedded && !returning" class="intro">{{ introCopy }}</p>

        <div class="fields">
            <div v-for="field in fields" :key="field.key" class="field" :class="{ 'role-field': field.key === 'role' }">
                <FormElement :title="field.label" :for="`identity-${field.key}`">
                    <input :id="`identity-${field.key}`" v-model="values[field.key]" class="field-input" type="text" :autocomplete="field.autocomplete" :name="field.key" />
                </FormElement>
            </div>
        </div>

        <fieldset class="manages">
            <legend id="manages-legend" class="manages-legend">{{ t("onboarding.calendarFirst.identityStep.managesLegend") }}</legend>

            <div class="manages-options" role="radiogroup" aria-labelledby="manages-legend">
                <button
                    v-for="(option, index) in managesOptions"
                    :key="option.value"
                    :ref="(el) => setOptionRef(el, index)"
                    type="button"
                    role="radio"
                    :value="option.value"
                    class="option"
                    :class="{ 'is-selected': managesChoice === option.value }"
                    :aria-checked="managesChoice === option.value"
                    :tabindex="tabStop(index) ? 0 : -1"
                    @click="managesChoice = option.value"
                    @keydown="onOptionKeydown($event, index)">
                    <span class="option-head">
                        <i aria-hidden="true" class="bi option-icon" :class="option.icon"></i>
                        <span class="option-label">{{ option.label }}</span>
                        <i v-if="managesChoice === option.value" aria-hidden="true" class="bi bi-check-lg option-check"></i>
                    </span>
                    <span class="option-body">{{ option.description }}</span>
                </button>
            </div>
        </fieldset>

        <StepActions>
            <BaseButton type="submit" theme="primary" :disabled="!ready || busy">
                {{ ctaLabel }}
            </BaseButton>
        </StepActions>
    </StepShell>
</template>

<script setup lang="ts">
import StepHeader from "~vue/CalendarFirstOnboarding/StepHeader.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BasePageHeader from "~vue/components/BasePageHeader.vue";
import FormElement from "~vue/components/form/FormElement.vue";
import { type ComponentPublicInstance, computed, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";

import StepActions from "./StepActions.vue";
import StepShell from "./StepShell.vue";
import type { IdentityPrefill } from "./types";

const {
    embedded = false,
    prefill,
    busy,
    returning = false,
    initialManagesPeople = null,
} = defineProps<{
    embedded?: boolean;
    prefill: IdentityPrefill;
    busy: boolean;
    /** Set when the user is already inside the product, so the screen stops introducing Nadia. */
    returning?: boolean;
    initialManagesPeople?: boolean | null;
}>();

const emit = defineEmits<{ submit: [{ firstName: string; lastName: string; role: string; managesPeople: boolean }] }>();

const { t } = useI18n();

type FieldKey = "firstName" | "lastName" | "role";

const fields = computed<{ key: FieldKey; label: string; autocomplete: string }[]>(() => [
    { key: "firstName", label: t("onboarding.calendarFirst.identityStep.fields.firstName"), autocomplete: "given-name" },
    { key: "lastName", label: t("onboarding.calendarFirst.identityStep.fields.lastName"), autocomplete: "family-name" },
    { key: "role", label: t("onboarding.calendarFirst.identityStep.fields.role"), autocomplete: "organization-title" },
]);

/*
 * A real radio group rather than a switch. A switch would default to off and
 * quietly record "does not manage people" for anyone who skipped it, and that
 * answer changes the coaching enough that we should hear it explicitly.
 */
type ManagesKey = "ic" | "manager";

const managesOptions = computed<{ value: ManagesKey; icon: string; label: string; description: string }[]>(() => [
    {
        value: "ic",
        icon: "bi-person",
        label: t("onboarding.calendarFirst.identityStep.manages.ic.label"),
        description: t("onboarding.calendarFirst.identityStep.manages.ic.description"),
    },
    {
        value: "manager",
        icon: "bi-people",
        label: t("onboarding.calendarFirst.identityStep.manages.manager.label"),
        description: t("onboarding.calendarFirst.identityStep.manages.manager.description"),
    },
]);

/* Prefilled from the directory — the point of this screen is confirming, not typing. */
const values = reactive<Record<FieldKey, string>>({
    firstName: prefill.first_name,
    lastName: prefill.last_name,
    role: prefill.role,
});

const managesChoice = ref<ManagesKey | undefined>(initialManagesPeople === null ? undefined : initialManagesPeople ? "manager" : "ic");
const managesPeople = computed(() => (managesChoice.value === undefined ? null : managesChoice.value === "manager"));

const optionRefs = ref<(HTMLButtonElement | null)[]>([]);

function setOptionRef(el: Element | ComponentPublicInstance | null, index: number): void {
    optionRefs.value[index] = el as HTMLButtonElement | null;
}

const ARROW_STEPS: Record<string, number> = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };

function tabStop(index: number): boolean {
    const checked = managesOptions.value.findIndex((option) => option.value === managesChoice.value);
    return index === (checked === -1 ? 0 : checked);
}

function onOptionKeydown(event: KeyboardEvent, index: number): void {
    const step = ARROW_STEPS[event.key] ?? 0;
    if (step === 0) {
        return;
    }
    event.preventDefault();
    const next = (index + step + managesOptions.value.length) % managesOptions.value.length;
    managesChoice.value = managesOptions.value[next].value;
    optionRefs.value[next]?.focus();
}

/*
 * Only a first name and the manages-people answer are required. This screen exists
 * to confirm what the directory already told us, and blocking on a surname or a
 * title -- neither of which every directory carries -- turns it back into a form.
 */
const hasFirstName = computed(() => values.firstName.trim().length > 0);
const ready = computed(() => hasFirstName.value && managesPeople.value !== null);

/*
 * The directory does not always carry a name, so on a cold account every field is
 * blank. A CTA that just says "Next" and does nothing is a dead end on the first
 * screen of onboarding, so the label names whichever answer is still missing.
 */
const ctaLabel = computed(() => {
    if (busy) {
        return t("onboarding.calendarFirst.identityStep.cta.busy");
    }
    if (!hasFirstName.value) {
        return t("onboarding.calendarFirst.identityStep.cta.needFirstName");
    }
    return managesPeople.value === null ? t("onboarding.calendarFirst.identityStep.cta.needChoice") : t("onboarding.calendarFirst.identityStep.cta.next");
});

/* Nothing came back from the directory, so there is nothing to "correct" -- asking
   them to check blank fields reads as a bug. */
/*
 * Keep one source attribution: previous field-level notices named a different system from the introduction.
 */
const introCopy = computed(() =>
    prefill.source.length > 0
        ? t("onboarding.calendarFirst.identityStep.intro.prefilled", { source: prefill.source })
        : returning
          ? t("onboarding.calendarFirst.identityStep.intro.returning")
          : t("onboarding.calendarFirst.identityStep.intro.fresh"),
);

function handleSubmit(): void {
    if (!ready.value || busy || managesPeople.value === null) {
        return;
    }
    emit("submit", {
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        role: values.role.trim(),
        managesPeople: managesPeople.value,
    });
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .heading {
        @apply text-center;
    }
    .intro {
        @apply body mb-6 text-ink-muted;
    }
    .step.is-embedded .intro {
        @apply body-small;
    }
    .fields {
        @apply mt-6 grid grid-cols-1 gap-x-10 gap-y-5 sm:grid-cols-2;
    }
    .field {
        @apply min-w-0;
    }
    .role-field {
        @apply sm:col-span-2;
    }
    .field-input {
        @apply body w-full border-0 border-b border-border-strong bg-transparent px-0 py-2 text-ink;
        @apply focus:border-ink focus:ring-0 focus:outline-none;
    }
    .manages {
        @apply mt-10;
    }
    .manages-legend {
        @apply body-small text-control-label;
    }
    .manages-options {
        @apply mt-3 grid gap-3 sm:grid-cols-2;
    }
    .option {
        @apply flex cursor-pointer flex-col rounded-lgr border border-border bg-surface-raised p-4 text-left transition-colors hover:border-border-strong;
        @apply focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:outline-none;
    }
    .option.is-selected {
        @apply border-accent bg-accent-soft/30;
    }
    .option-head {
        @apply flex items-center gap-2;
    }
    .option-icon {
        @apply shrink-0 text-ink-muted;
    }
    .option.is-selected .option-icon,
    .option-check {
        @apply text-accent;
    }
    .option-check {
        @apply ml-auto;
    }
    .option-label {
        @apply body-small-bold text-ink;
    }
    .option-body {
        @apply body-small mt-2 text-ink-muted;
    }
}
</style>
